import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Oval } from 'react-loader-spinner';
import MobileInput from '../../components/InputGroup/MobileInput';
import { sendOtpForLoginForTabletForm, verifyOtpForLoginForTabletForm } from '../../services/public/public.service';
import { getDeviceInfo } from '../../helpers/helpers';
import { getSvFormConfig } from '../../services/private/site-visit.service';

import ramaLogo from '../../assets/plus_icon/rama_logo.png';
import { ReactComponent as LogoIcon } from '../../assets/logo.svg';
import SVWelcomeImage from '../../assets/plus_icon/sv_welcome.png';
import SVWelcomeMobile from '../../assets/plus_icon/mobile_welcome.png';
import SVWelcomeDesktop from '../../assets/plus_icon/desktop_welcom.png';

const numberOfDigits = 4;

const LoginFlow = () => {
    const [step, setStep] = useState('enter_mobile'); // 'enter_mobile', 'enter_otp'
    const [mobile, setMobile] = useState('');
    const [error, setError] = useState('');
    const [otp, setOtp] = useState(new Array(numberOfDigits).fill(''));
    const [otpError, setOtpError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deviceInfo, setDeviceInfo] = useState({});
    const [countDown, setCountDown] = useState(30);
    const [disableResend, setDisableResend] = useState(true);
    const [branding, setBranding] = useState(null);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [imageLoaded, setImageLoaded] = useState(false);

    const otpBoxReference = useRef([]);
    const history = useHistory();
    const { projectKey } = useParams();

    // Redirect if already logged in
    useEffect(() => {
        if (localStorage.getItem('authToken')) {
            history.push('/site-visit');
        }
    }, [history]);

    // Get Device Info
    useEffect(() => {
        getDeviceInfo().then((res) => {
            setDeviceInfo(res);
        });
    }, []);

    // Get Branding config for login screen
    useEffect(() => {
        if (!projectKey) {
            history.push('/404');
            return;
        }

        const PROJECT_ID_MAP = {
            default: 2058,
            yf: 2364,
            av: 2366,
            eg: 2368,
            kera: 2372,
            rivereast: 2376
        };

        let id;
        if (!isNaN(projectKey)) {
            id = parseInt(projectKey, 10);
        } else {
            id = PROJECT_ID_MAP[projectKey];
        }

        if (!id) {
            history.push('/404');
            return;
        }

        setLoadingConfig(true);
        getSvFormConfig({ project_id: id })
            .then(res => {
                if (res.data.status === 200) {
                    const brandData = res.data.data?.branding;
                    setBranding(brandData);
                    if (!brandData?.banners?.desktop && !brandData?.banners?.tablet && !brandData?.banners?.mobile && !brandData?.cover && !brandData?.brand_logo) {
                        setImageLoaded(true);
                    }
                } else {
                    history.push('/404');
                }
            })
            .catch(err => {
                console.error("Failed to load branding in login screen", err);
                history.push('/404');
            })
            .finally(() => {
                setLoadingConfig(false);
            });
    }, [projectKey, history]);

    // OTP Timer
    useEffect(() => {
        let timer;
        if (step === 'enter_otp' && countDown > 0) {
            timer = setInterval(() => {
                setCountDown((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        setDisableResend(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [step, countDown]);

    // Send OTP handler
    const handleSendOTP = async () => {
        if (!mobile) {
            setError('Please enter mobile number.');
            return;
        }
        setError('');
        setLoading(true);
        try {
            const res = await sendOtpForLoginForTabletForm({ mobile });
            if (res.data.status === 200) {
                toast.success(res.data.message || 'OTP sent successfully.');
                setCountDown(30);
                setDisableResend(true);
                setOtp(new Array(numberOfDigits).fill(''));
                setOtpError(false);
                setStep('enter_otp');
            }
        } catch (err) {
            console.error({ err });
            toast.error(err?.response?.data?.message || 'Failed to send OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Verify OTP handler
    const handleVerifyOTP = async () => {
        const otpStr = otp.join('');
        if (otpStr.length < numberOfDigits) {
            setOtpError(true);
            return;
        }
        setOtpError(false);
        setLoading(true);
        try {
            const res = await verifyOtpForLoginForTabletForm({ mobile, otp: otpStr });
            if (res.data.status === 200) {
                const token = res.data.data?.token || res.data.token;
                if (token) {
                    localStorage.setItem('authToken', token);
                    window.location.href = `/site-visit/${projectKey}`;
                    return;
                } else {
                    toast.error('Token not found in response.');
                }
            }
        } catch (err) {
            console.error({ err });
            setOtpError(true);
            toast.error(err?.response?.data?.message || 'Incorrect OTP, Please Retry.');
        }
        setLoading(false);
    };

    // Helper functions for OTP inputs
    const handleOTPChange = (value, index) => {
        let newOtp = [...otp];
        newOtp[index] = value.slice(-1);
        setOtp(newOtp);

        if (value && index < numberOfDigits - 1) {
            otpBoxReference.current[index + 1]?.focus();
        }
    };

    const handleBackspaceAndEnter = (e, index) => {
        if (e.key === 'Backspace' && !e.target.value && index > 0) {
            otpBoxReference.current[index - 1]?.focus();
        }
        if (e.key === 'Enter' && e.target.value && index < numberOfDigits - 1) {
            otpBoxReference.current[index + 1]?.focus();
        }
    };

    const showSkeleton = loadingConfig || (branding && !imageLoaded);

    return (
        <div className='bg-[#F8FAFB] w-full sm:w-[1024px] mx-auto min-h-screen overflow-auto relative font-inter'>
            {/* Embedded styles for top notch Behance layout & element custom styling */}
            <style>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(16px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in-up {
                    animation: fadeInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                @keyframes shimmer {
                    0% {
                        background-position: -200% 0;
                    }
                    100% {
                        background-position: 200% 0;
                    }
                }
                .shimmer-loader {
                    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%) !important;
                    background-size: 200% 100% !important;
                    animation: shimmer 1.5s infinite linear !important;
                }
                
                /* Custom PhoneInput layout overrides to make it sleek and align width */
                .react-tel-input {
                    width: 100% !important;
                }
                .react-tel-input .form-control {
                    height: 52px !important;
                    width: 100% !important;
                    border-radius: 12px !important;
                    border: 1px solid #CBD5E1 !important;
                    font-size: 16px !important;
                    font-weight: 500 !important;
                    color: #1E293B !important;
                    padding-left: 56px !important;
                    transition: all 0.2s ease-in-out !important;
                }
                .react-tel-input .form-control:focus {
                    border-color: #6D4DFF !important;
                    box-shadow: 0 0 0 4px rgba(109, 77, 255, 0.12) !important;
                }
                .react-tel-input .flag-dropdown {
                    border: 1px solid #CBD5E1 !important;
                    border-right: none !important;
                    border-radius: 12px 0 0 12px !important;
                    background-color: #F8FAFC !important;
                    height: 52px !important;
                    width: 46px !important;
                }
                .react-tel-input .flag-dropdown.open {
                    border-radius: 12px 0 0 0 !important;
                }
                .react-tel-input .selected-flag {
                    width: 46px !important;
                    border-radius: 12px 0 0 12px !important;
                }
                
                /* Custom OTP inputs styling */
                .otp-input-premium {
                    width: 60px;
                    height: 52px;
                    border: 1px solid #CBD5E1;
                    border-radius: 12px;
                    font-size: 20px;
                    font-weight: 600;
                    color: #1E293B;
                    background: #FFFFFF;
                    transition: all 0.2s ease-in-out;
                    outline: none;
                    text-align: center;
                    padding: 0 !important;
                }
                .otp-input-premium:focus {
                    border-color: #6D4DFF;
                    box-shadow: 0 0 0 4px rgba(109, 77, 255, 0.12);
                    transform: scale(1.05);
                }

                .btn-premium {
                    height: 52px;
                    border-radius: 12px;
                    font-size: 16px;
                    font-weight: 600;
                    color: #FFFFFF;
                    background: linear-gradient(135deg, #6D4DFF 0%, #0066FF 100%);
                    box-shadow: 0 4px 16px rgba(109, 77, 255, 0.2);
                    transition: all 0.2s ease-in-out;
                    border: none;
                    outline: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .btn-premium:hover:not(:disabled) {
                    box-shadow: 0 6px 20px rgba(109, 77, 255, 0.3);
                    transform: translateY(-1px);
                    filter: brightness(1.03);
                }
                .btn-premium:active:not(:disabled) {
                    transform: translateY(0);
                }
                .btn-premium:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    pointer-events: none;
                }

                .premium-card {
                    background: #ffffff;
                    border: 1px solid rgba(226, 232, 240, 0.8);
                    box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.05);
                    border-radius: 20px;
                    padding: 24px;
                }
                @media (min-width: 640px) {
                    .premium-card {
                        padding: 32px;
                    }
                }

                /* Premium react-phone-input overrides */
                .react-tel-input {
                    font-family: 'Inter', sans-serif !important;
                }
                .react-tel-input .form-control {
                    width: 100% !important;
                    height: 52px !important;
                    border: 1.5px solid #E2E8F0 !important;
                    border-radius: 12px !important;
                    font-size: 16px !important;
                    color: #1E293B !important;
                    padding-left: 58px !important; /* Balanced left padding when dropdown arrow is hidden */
                    background: #FFFFFF !important;
                    transition: all 0.2s ease-in-out !important;
                }
                .react-tel-input .form-control:focus {
                    border-color: #6D4DFF !important;
                    box-shadow: 0 0 0 4px rgba(109, 77, 255, 0.12) !important;
                }
                .react-tel-input .flag-dropdown {
                    background: transparent !important;
                    border: none !important;
                    border-radius: 12px 0 0 12px !important;
                    padding: 0 !important;
                    height: 100% !important;
                    display: flex !important;
                    align-items: center !important;
                }
                .react-tel-input .selected-flag {
                    background: transparent !important;
                    width: auto !important;
                    height: 100% !important;
                    padding: 0 10px 0 10px !important; /* Balanced horizontal padding around the flag */
                    border-radius: 8px 0 0 8px !important;
                    display: flex !important;
                    align-items: center !important; /* Vertical center alignment */
                    justify-content: center !important;
                    transition: background-color 0.2s !important;
                    position: relative !important;
                }
                .react-tel-input .selected-flag:hover,
                .react-tel-input .selected-flag:focus {
                    background-color: #F1F5F9 !important;
                }
                .react-tel-input .selected-flag::after {
                    content: "" !important;
                    position: absolute !important;
                    right: 0 !important;
                    top: 15px !important;
                    height: 22px !important;
                    width: 1.5px !important;
                    background-color: #E2E8F0 !important;
                }
                .react-tel-input .selected-flag .flag {
                    transform: scale(1.3) !important; /* Large crisp flag */
                    position: static !important; /* Center flag via flexbox */
                    margin: 0 !important;
                }
                /* Hide the dropdown arrow caret icon completely */
                .react-tel-input .selected-flag .arrow {
                    display: none !important;
                }
                .react-tel-input .country-list {
                    border-radius: 12px !important;
                    border: 1px solid rgba(226, 232, 240, 0.8) !important;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
                    padding: 6px !important;
                    margin-top: 6px !important;
                    background: #FFFFFF !important;
                    width: 250px !important;
                    max-height: 220px !important;
                }
                .react-tel-input .country-list::-webkit-scrollbar {
                    width: 5px !important;
                }
                .react-tel-input .country-list::-webkit-scrollbar-track {
                    background: transparent !important;
                }
                .react-tel-input .country-list::-webkit-scrollbar-thumb {
                    background: #CBD5E1 !important;
                    border-radius: 10px !important;
                }
                .react-tel-input .country-list .country {
                    border-radius: 8px !important;
                    padding: 10px 12px !important;
                    transition: all 0.2s ease-in-out !important;
                    display: flex !important;
                    align-items: center !important;
                    gap: 12px !important;
                }
                .react-tel-input .country-list .country:hover {
                    background-color: rgba(109, 77, 255, 0.06) !important;
                    padding-left: 14px !important;
                }
                .react-tel-input .country-list .country.highlight {
                    background-color: rgba(109, 77, 255, 0.1) !important;
                    color: #6D4DFF !important;
                    font-weight: 500 !important;
                }
                .react-tel-input .country-list .country .flag {
                    margin-right: 0 !important;
                    transform: scale(1.2) !important;
                }
                .react-tel-input .country-list .country-name {
                    font-size: 14px !important;
                    color: #1E293B !important;
                    font-weight: 500 !important;
                }
                /* Hide dial code inside country list dropdown */
                .react-tel-input .country-list .dial-code {
                    display: none !important;
                }
            `}</style>

            {/* Top Hero Banners - preserving original size layout */}
            {showSkeleton && (
                <div className="w-full h-[250px] sm:h-[400px] xl:h-[500px] shimmer-loader rounded-xl"></div>
            )}
            <div style={{ display: showSkeleton ? 'none' : 'block' }}>
                <figure className='hidden xl:block'>
                    <img 
                        src={loadingConfig ? undefined : (branding?.banners?.desktop || branding?.cover || SVWelcomeDesktop)} 
                        alt="" 
                        className='w-full' 
                        onLoad={() => setImageLoaded(true)} 
                        onError={() => setImageLoaded(true)} 
                    />
                </figure>
                <figure className='hidden sm:block xl:hidden'>
                    <img 
                        src={loadingConfig ? undefined : (branding?.banners?.tablet || branding?.cover || SVWelcomeImage)} 
                        alt="" 
                        className='w-full' 
                        onLoad={() => setImageLoaded(true)} 
                        onError={() => setImageLoaded(true)} 
                    />
                </figure>
                <figure className='sm:hidden'>
                    <img 
                        src={loadingConfig ? undefined : (branding?.banners?.mobile || branding?.cover || SVWelcomeMobile)} 
                        alt="" 
                        className='w-full' 
                        onLoad={() => setImageLoaded(true)} 
                        onError={() => setImageLoaded(true)} 
                    />
                </figure>
            </div>

            <div className='flex flex-col justify-between pb-10'>
                {/* Logo Section - restored to original layout & sizes from WelcomePage */}
                <div className='flex items-center justify-center !my-10'>
                    {showSkeleton && (
                        <div className="h-[62px] sm:h-[90px] w-[228px] shimmer-loader rounded-lg"></div>
                    )}
                    <figure className='m-0 h-[62px] sm:h-[90px] w-[228px]' style={{ display: showSkeleton ? 'none' : 'block' }}>
                        <img 
                            src={loadingConfig ? undefined : (branding?.brand_logo || ramaLogo)} 
                            alt="Logo" 
                            className='h-100 w-100 object-contain' 
                            onLoad={() => setImageLoaded(true)} 
                            onError={() => setImageLoaded(true)} 
                        />
                    </figure>
                    <div className='border-r-2 border-[#CFD5DE] h-[80px] sm:h-[100px] !ml-7 !mr-12'></div>
                    <div>
                        <LogoIcon className='sm:w-[200px] sm:h-[90px] w-[130px] h-[62px]' />
                    </div>
                </div>

                {/* Form Fields container - perfectly width-aligned with WelcomePage get started button */}
                <div className='w-[95%] sm:w-[396px] mx-auto !mt-1 !mb-6 sm:!my-6 premium-card animate-fade-in-up'>
                    
                    {step === 'enter_mobile' && (
                        <div>
                            <h2 className='text-2xl font-bold text-slate-800 tracking-tight mb-1'>Login</h2>
                            <p className='text-sm text-slate-400 mb-6'>Enter mobile number to receive OTP</p>
                            
                            <div className='font-inter [&>*]:!border-0 mb-6 w-full'>
                                <MobileInput
                                    value={mobile}
                                    onChange={(e) => setMobile(e.phone)}
                                    dropdownDisable={false}
                                    onlyCountries={['in', 'ae', 'us', 'ca', 'gb']}
                                    error={error}
                                    classname="!w-full"
                                    inputclass='!h-[52px] !w-full'
                                />
                            </div>
                            
                            <button
                                onClick={handleSendOTP}
                                disabled={loading}
                                className='w-full btn-premium'
                            >
                                {loading ? (
                                    <Oval height={22} width={22} color="#FFFFFF" secondaryColor="#FFFFFF" strokeWidth={4} />
                                ) : (
                                    'Send OTP'
                                )}
                            </button>
                        </div>
                    )}

                    {step === 'enter_otp' && (
                        <div>
                            <div className='flex items-center justify-between mb-1.5'>
                                <h2 className='text-2xl font-bold text-slate-800 tracking-tight'>Confirm OTP</h2>
                                <button
                                    onClick={() => setStep('enter_mobile')}
                                    className='text-sm font-semibold text-[#6D4DFF] hover:text-[#5536DA] transition-colors'
                                >
                                    Edit Number
                                </button>
                            </div>
                            <p className='text-sm text-slate-500 mb-3'>Enter the OTP sent to +{mobile}</p>
                            
                            <div className='flex justify-center gap-2.5 my-2.5'>
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        value={digit}
                                        maxLength={1}
                                        type='text'
                                        pattern='[0-9]*'
                                        inputMode='numeric'
                                        onChange={(e) => handleOTPChange(e.target.value.replace(/[^0-9]/g, ''), index)}
                                        onKeyUp={(e) => handleBackspaceAndEnter(e, index)}
                                        ref={(ref) => (otpBoxReference.current[index] = ref)}
                                        className='otp-input-premium'
                                    />
                                ))}
                            </div>
                            {otpError && (
                                <p className='text-xs text-center font-medium mt-2' style={{color: '#EF4444'}}>Incorrect OTP, Please Retry.</p>
                            )}

                            <div className='text-center mt-4 mb-6 text-sm h-5 flex items-center justify-center'>
                                {disableResend ? (
                                    <span className='text-slate-400'>Resend OTP in <span className='font-semibold text-slate-600'>{countDown}s</span></span>
                                ) : (
                                    <button
                                        onClick={handleSendOTP}
                                        className='text-[#6D4DFF] font-semibold hover:underline'
                                    >
                                        Resend OTP
                                    </button>
                                )}
                            </div>

                            <button
                                onClick={handleVerifyOTP}
                                disabled={loading}
                                className='w-full btn-premium'
                            >
                                {loading ? (
                                    <Oval height={22} width={22} color="#FFFFFF" secondaryColor="#FFFFFF" strokeWidth={4} />
                                ) : (
                                    'Verify & Login'
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginFlow;
